import { useCallback } from 'react'
import { getNextSortState } from './data-table.helper'
import type { SortOrder } from './data-table.interface'

interface UseDataTableSortProps {
  sortBy?: string
  sortOrder?: SortOrder
  onSortChange?: (sortBy: string, sortOrder: SortOrder) => void
}

interface UseDataTableSortReturn {
  handleSort: (columnId: string) => void
  getColumnSortOrder: (columnId: string) => SortOrder | null
}

/**
 * Hook for managing data table sorting state and handlers.
 * Provides functionality to handle column sort clicks and retrieve current sort state.
 */
export const useDataTableSort = ({
  sortBy,
  sortOrder,
  onSortChange,
}: UseDataTableSortProps): UseDataTableSortReturn => {
  const handleSort = useCallback(
    (columnId: string): void => {
      if (!onSortChange) return
      const { sortBy: newSortBy, sortOrder: newSortOrder } = getNextSortState(
        columnId,
        sortBy,
        sortOrder
      )
      onSortChange(newSortBy, newSortOrder)
    },
    [onSortChange, sortBy, sortOrder]
  )

  const getColumnSortOrder = useCallback(
    (columnId: string): SortOrder | null => (sortBy === columnId ? (sortOrder ?? null) : null),
    [sortBy, sortOrder]
  )

  return {
    handleSort,
    getColumnSortOrder,
  }
}
